import { useState, useEffect } from "react";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, MessageCircle, Heart, Users, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MatchesView({ token, onOpenChat }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const res = await fetch(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMatches(data.matches || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-6 md:p-10" data-testid="matches-view">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Heart className="w-5 h-5 text-[#00FF66]" />
          <span
            className="text-xs uppercase tracking-[0.2em] text-[#00FF66]"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            Your Connections
          </span>
        </div>
        <h1
          style={{ fontFamily: "'Bodoni Moda', serif" }}
          className="text-4xl sm:text-5xl tracking-tighter text-[#FAFAFA] mb-10"
        >
          Matches
        </h1>

        {loading ? (
          <p
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
            className="text-[#00FF66] text-sm tracking-widest uppercase animate-pulse"
          >
            Loading matches...
          </p>
        ) : matches.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-[#8A8A93] mx-auto mb-4" />
            <h3
              style={{ fontFamily: "'Bodoni Moda', serif" }}
              className="text-xl text-[#FAFAFA] mb-2"
            >
              No matches yet
            </h3>
            <p className="text-[#8A8A93] text-sm">
              Start swiping to find your match!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {matches.map((match) => (
              <button
                key={match.match_id}
                data-testid={`match-card-${match.match_id}`}
                onClick={() => onOpenChat(match.match_id, match.user)}
                className="group relative bg-[#0C0D10] border border-[#1F2027] overflow-hidden text-left transition-colors hover:border-[#00FF66]/30"
              >
                <div className="aspect-[3/4] w-full overflow-hidden relative">
                  {match.both_revealed && match.user?.photos?.[0] ? (
                    <img
                      src={match.user.photos[0]}
                      alt={match.user?.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div
                      className="w-full h-full relative overflow-hidden"
                      data-testid={`match-photo-blurred-${match.match_id}`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-[#1A0F2E] via-[#2A0F1D] to-[#0A0A0C]" />
                      <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full bg-[#FF3B6B]/25 blur-[80px]" />
                      <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-[#7C3AED]/25 blur-[80px]" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                        <EyeOff size={32} className="text-white/40" strokeWidth={1.2} />
                        <span
                          className="text-white/50 text-[10px] uppercase tracking-[0.2em]"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}
                        >
                          Photo locked
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-[#FAFAFA] font-medium">
                        {match.user?.name}
                      </h3>
                      <span className="text-[#8A8A93] text-sm">
                        {match.user?.age}
                      </span>
                    </div>
                    <ShieldCheck className="w-4 h-4 text-[#00FF66]" />
                  </div>
                  {match.last_message && (
                    <p className="text-[#8A8A93] text-xs truncate mt-1">
                      {match.last_message.content}
                    </p>
                  )}
                  {!match.last_message && (
                    <div className="flex items-center gap-1.5 text-[#00FF66] text-xs mt-1">
                      <MessageCircle size={12} />
                      <span
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                        className="tracking-wider uppercase"
                      >
                        Send first message
                      </span>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
